import { useEffect, useMemo, useState } from 'react'
import { List, fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import _isArray from 'lodash/isArray'
import { useLazyQuery } from '@apollo/client'
import {
  formatBillingPlans,
  userPlanGridPayloadForFormat,
  BILLING_PLAN_GRID_LOCATION_CHECKOUT,
  PRICE_REPLACEMENT_TYPE_NONE,
} from './utils'
import {
  GET_PLANS,
  GET_PLANS_BY_SKU,
  GET_PLAN_ELIGIBILITY,
  GET_USER_PLAN_GRID_LIST,
} from './query'
import { get as getConfig } from '../../config/index'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_FETCH_POLICY_NO_CACHE,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../apollo-graphql/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

/** Stable empty eligibility; inline `Map()` breaks reference equality in effects. */
const EMPTY_ELIGIBILITY_MAP = Map()

// Utility function to detect Apollo Client store reset errors
const isApolloStoreResetError = (error) => {
  if (!error) return false

  // Check main error message
  if (error.message) {
    const message = error.message.toLowerCase()
    if (message.includes('store reset while query was in flight')
        || message.includes('not completed in link chain')
        || message.includes('invariant violation')
        || message.includes('apollo client has been stopped')
        || message.includes('query was cancelled')
        || message.includes('network request failed')) {
      return true
    }
  }

  // Check GraphQL errors
  if (error.graphQLErrors && Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors.some((err) => err.message && (
      err.message.toLowerCase().includes('store reset while query was in flight')
      || err.message.toLowerCase().includes('not completed in link chain')
    ))
  }

  // Check network errors
  if (error.networkError) {
    const { networkError } = error
    if (networkError.message) {
      const message = networkError.message.toLowerCase()
      if (message.includes('store reset while query was in flight')
          || message.includes('not completed in link chain')
          || message.includes('network request failed')) {
        return true
      }
    }
  }

  // Check for Apollo Client's internal error metadata
  if (error.extraInfo && error.extraInfo.storeReset) {
    return true
  }

  return false
}

/**
 * React hook for fetching user-scoped plan grid list (mirrors useGetPlanGridData + GET_PLANS).
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} options.authJwt - The user's auth jwt
 * @param {String} options.page - The location/page for the plan grid (checkout, etc)
 * @param {Boolean} options.forceRefresh - Whether to force refresh the plans
 * @returns {List} - Formatted List of plans
 */
export function useGetUserPlanGridList (options) {
  const {
    userLanguage,
    authJwt,
    page = BILLING_PLAN_GRID_LOCATION_CHECKOUT,
    forceRefresh,
  } = options
  const [
    getUserPlanGridListData,
    { data: gqlUserPlanGridListData, error },
  ] = useLazyQuery(GET_USER_PLAN_GRID_LIST, {
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const [retryCount, setRetryCount] = useState(0)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt

  const { plansData, bestValueBillingProviderProductId } = userPlanGridPayloadForFormat(
    gqlUserPlanGridListData,
  )
  const formattedPlans = fromJS(
    formatBillingPlans(plansData, bestValueBillingProviderProductId),
  ) || List()

  useEffect(() => {
    if (planGraphQLActive) {
      getUserPlanGridListData({
        variables: {
          language: userLanguage,
          page,
        },
        fetchPolicy: authJwtChanged || forceRefresh
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [
    userLanguageChanged,
    authJwtChanged,
    forceRefresh,
    getUserPlanGridListData,
    userLanguage,
    page,
    authJwt,
  ])

  useEffect(() => {
    if (isApolloStoreResetError(error) && retryCount < 3) {
      const delay = Math.min(100 * (2 ** retryCount), 1000)
      const timeoutId = setTimeout(() => {
        setRetryCount((prev) => prev + 1)
        getUserPlanGridListData({
          variables: {
            language: userLanguage,
            page,
          },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: {
            jwt: authJwt,
          },
        })
      }, delay)

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [error, retryCount, userLanguage, page, authJwt, getUserPlanGridListData])

  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
    setRetryCount(0)
  }, [authJwtChanged])

  return formattedPlans
}

/**
 * React hook for fetching plans for the plan grid (checkout, changePlan, gift)
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {String} option.page - The location/page for the plan grid (checkout, etc)
 * @param {Boolean} option.forceRefresh - Whether to force refresh the plans
 * @returns {List} - Formatted List of plans
 */
export function useGetPlanGridData (options) {
  const {
    userLanguage,
    authJwt,
    page = BILLING_PLAN_GRID_LOCATION_CHECKOUT,
    forceRefresh,
  } = options
  const [getPlansData, { data: gqlPlanData, error }] = useLazyQuery(GET_PLANS, {
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const [retryCount, setRetryCount] = useState(0)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt

  // Format the plans data
  const formattedPlans = fromJS(formatBillingPlans(gqlPlanData)) || List()

  useEffect(() => {
    if (planGraphQLActive) {
      getPlansData({
        variables: {
          language: userLanguage,
          planGrid: true,
          page,
        },
        fetchPolicy: authJwtChanged || forceRefresh
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged, authJwtChanged, forceRefresh, getPlansData, userLanguage, page, authJwt])

  // Handle store reset errors and retry the query
  useEffect(() => {
    if (isApolloStoreResetError(error) && retryCount < 3) {
      // Wait a bit for the store to be fully reset, then retry
      // Use exponential backoff for retries
      const delay = Math.min(100 * (2 ** retryCount), 1000)
      const timeoutId = setTimeout(() => {
        setRetryCount((prev) => prev + 1)
        getPlansData({
          variables: {
            language: userLanguage,
            planGrid: true,
            page,
          },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: {
            jwt: authJwt,
          },
        })
      }, delay)

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [error, retryCount, userLanguage, page, authJwt, getPlansData])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  // set the user's jwt for this page to help handle loading plans
  // if the user's jwt changes, then load plans again
  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
    // Reset retry count when JWT changes
    setRetryCount(0)
  }, [authJwtChanged])

  return formattedPlans
}

/**
 * React hook for fetching plans by sku
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {String|String[]} option.planSkus - A single sku (string) or an Array of sku strings
 * @param {Boolean} option.forceRefresh - Whether to force refresh the plans
 * @returns {List} - Formatted List of plans
 */
export function useGetPlanDataBySku (options) {
  const {
    planSkus,
    userLanguage,
    authJwt,
    forceRefresh,
    priceReplacementType = PRICE_REPLACEMENT_TYPE_NONE,
  } = options
  const [
    getPlansDataBySku,
    { data: planData, loading, error },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const formattedPlans = fromJS(formatBillingPlans(planData)) || List()
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt
  const skus = _isArray(planSkus) ? planSkus : [planSkus]

  useEffect(() => {
    if (planGraphQLActive && planSkus) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus,
          priceReplacementType,
        },
        fetchPolicy: authJwtChanged || forceRefresh
          ? APOLLO_CLIENT_FETCH_POLICY_NO_CACHE
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [planSkus, userLanguageChanged, authJwtChanged, forceRefresh])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  // set the user's jwt for this page to help handle loading plans
  // if the user's jwt changes, then load plans again
  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
  }, [authJwtChanged])

  return Map({
    plans: formattedPlans,
    loading,
    error,
    complete: planSkus ? !loading && !!planData : true,
  })
}

/**
 * Fetches signed-in plan eligibility for a SKU and page (checkout vs plan change).
 * @param {Object} options
 * @param {string|null|undefined} options.sku - Plan SKU (cleaned); when missing, hook is idle.
 * @param {string|null|undefined} options.authJwt - Customer JWT forwarded on the request.
 * @param {string} options.page - GraphQL PlanEligibilityPage (e.g. planChange, checkout).
 * @param {boolean} [options.enabled=true] - When false, no request and complete is true.
 * @param {boolean} [options.forceRefresh=false]
 * @returns {Immutable.Map} Keys: eligibility (Map), loading, error, complete
 */
export function useGetPlanEligibility (options) {
  const {
    sku,
    authJwt,
    page,
    enabled = true,
    forceRefresh,
  } = options
  const [
    getPlanEligibility,
    {
      data, loading, error, called,
    },
  ] = useLazyQuery(GET_PLAN_ELIGIBILITY, {
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })
  const [planEligibilityAuthJwt, setPlanEligibilityAuthJwt] = useState(authJwt)
  const authJwtChanged = authJwt !== planEligibilityAuthJwt

  const eligibility = useMemo(() => {
    const raw = _get(data, 'billingProviderProductEligibility')
    return raw ? fromJS(raw) : EMPTY_ELIGIBILITY_MAP
  }, [data])

  const idle = !planGraphQLActive || !enabled || !sku
  let complete = false
  if (idle) {
    complete = true
  } else if (called) {
    complete = !loading
  }

  useEffect(() => {
    if (!idle) {
      getPlanEligibility({
        variables: { sku, page },
        fetchPolicy: authJwtChanged || forceRefresh
          ? APOLLO_CLIENT_FETCH_POLICY_NO_CACHE
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [sku, page, enabled, authJwtChanged, forceRefresh, idle, getPlanEligibility, authJwt])

  useEffect(() => {
    setPlanEligibilityAuthJwt(authJwt)
  }, [authJwt])

  return Map({
    eligibility,
    loading,
    error,
    complete,
  })
}

/**
 * React hook for fetching plans for the plan grid (checkout, changePlan, gift)
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {Map} option.selectedPlan - The user's selectedPlan
 * @param {String} option.userAccountCurrencyIso - The user's account currency iso
 * @param {Function} options.setPlansSelection - Set plan selection action
 */
export function useUpdateSelectedPlan (options) {
  const {
    selectedPlan = Map(),
    setPlansSelection,
    userLanguage,
    authJwt,
    userAccountCurrencyIso,
  } = options
  const [
    getPlansDataBySku,
    { data: planData },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const [selectedPlanLanguage, setSelectedPlanLanguage] = useState(userLanguage)
  const selectedPlanSku = selectedPlan.get('sku')
  const userLanguageChanged = userLanguage !== selectedPlanLanguage
  const formattedPlans = fromJS(formatBillingPlans(planData)) || List()
  const incomingPlan = userLanguageChanged ? Map() : formattedPlans.find((p) => p.get('sku') === selectedPlanSku) || Map()

  useEffect(() => {
    if (planGraphQLActive && selectedPlanSku) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus: selectedPlanSku,
        },
        fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged, userAccountCurrencyIso])

  // set the user language for this the selected to help handle loading plans
  // if the user changes their language
  useEffect(() => {
    setSelectedPlanLanguage(userLanguage)
  }, [userLanguage])

  // set the user's selected plan in Redux if the language changed
  if (Map.isMap(incomingPlan) && incomingPlan.size && incomingPlan.get('billingSchedule') !== selectedPlan.get('billingSchedule')) {
    setPlansSelection(incomingPlan)
  }

  // set the user's selected plan if the currency changed
  if (Map.isMap(incomingPlan) && incomingPlan.size && incomingPlan.get('currencyIso') !== selectedPlan.get('currencyIso')) {
    setPlansSelection(incomingPlan)
  }
}
